"use client";

import { useState } from "react";
import { Check, ClipboardCopy } from "lucide-react";
import { FaViber, FaWhatsapp } from "react-icons/fa6";
import { cn } from "@/lib/utils";

const CONTACT_TEXT = `
PODACI O USLUZI:

- Naziv biznisa/usluge:
- Opis vaše usluge, cijene i druge informacije:
- Grad/ovi u kojima poslujete:
- Adresa/e:
- Kontakt broj/evi:
- Viber:
- WhatsApp:
- Email:
- Instagram (link profila ili @korisničko ime):
- Facebook (link od stranice):
- Fotografije

Napomena: Ukoliko nemate neke od infomacija, nije obavezno da pošaljete. Takođe, dodajte šta god smatrate da bi unaprijedilo opis vašeg biznisa/usluge.`;

const VIBER_NUMBER = "38766133034";
const WHATSAPP_NUMBER = "38766133034";

/** Softer label colors on dark cards (avoids muddy low-opacity blues). */
const STEP_LABEL_DARK = ["#A8B4FF", "#4ADE80", "#FCD34D", "#C4B5FD"] as const;

/*
 * `accent` is the bright brand colour we use for the border / dark-mode UI.
 * `textAccent` is the WCAG-AA-compliant variant (≥ 4.5 : 1 against the white
 * card background) used for the step-number badge and label text. The previous
 * setup used `accent` for both, which failed Lighthouse contrast for the green,
 * amber, and violet steps.
 */
type Step = {
  number: string;
  label: string;
  title: string;
  description: string;
  accent: string;
  textAccent: string;
  accentBg: string;
  accentBorder: string;
};

const steps: Step[] = [
  {
    number: "01",
    label: "Prvi korak",
    title: "Pripremite opis usluge",
    description:
      "Jednim klikom kopirajte pripremljenu poruku sa potrebnim informacijama ili samostalno opišite Vašu uslugu.",
    accent: "#314BFF",
    // Already passes AA on white; keep as-is.
    textAccent: "#2438C9",
    accentBg: "rgba(49,75,255,0.07)",
    accentBorder: "rgba(49,75,255,0.18)",
  },
  {
    number: "02",
    label: "Drugi korak",
    title: "Kontaktirajte nas",
    description:
      "Zatim nas kontaktirajte na Viber ili WhatsApp i pošaljite podatke o Vašoj usluzi.",
    accent: "#22C55E",
    // green-700 → 4.7 : 1 contrast on white (vs ~2.5 for green-500).
    textAccent: "#15803D",
    accentBg: "rgba(34,197,94,0.10)",
    accentBorder: "rgba(34,197,94,0.22)",
  },
  {
    number: "03",
    label: "Treći korak",
    title: "Pošaljite opis i podatke",
    description:
      "Pošaljite opis i podatke o Vašoj usluzi koji ste pripremili u prvom koraku.",
    accent: "#F59E0B",
    // amber-700 → 4.8 : 1 contrast on white (vs ~2.1 for amber-500).
    textAccent: "#B45309",
    accentBg: "rgba(245,158,11,0.10)",
    accentBorder: "rgba(245,158,11,0.22)",
  },
  {
    number: "04",
    label: "Četvrti korak",
    title: "Obrada informacija i objavljivanje - mi radimo sve za Vas",
    description:
      "Mi obradimo informacije o Vašoj usluzi i objavimo je. Nakon objave, dobijate obavještenje i direktan link do Vaše stranice.",
    accent: "#8B5CF6",
    // violet-700 → 5.4 : 1 contrast on white.
    textAccent: "#6D28D9",
    accentBg: "rgba(139,92,246,0.10)",
    accentBorder: "rgba(139,92,246,0.22)",
  },
];

export function ObjaviUsluguSection() {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(CONTACT_TEXT);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      /* noop */
    }
  };

  return (
    <section id="objavi-uslugu" className="scroll-mt-20 py-12 md:py-19">
      <div className="mx-auto w-full max-w-[min(100%,96rem)] px-3">
        {/* ── Header — matches ServicesSection style ── */}
        <div className="mb-8 grid items-center justify-center gap-4 md:mb-10 lg:grid-cols-[minmax(0,1fr)_minmax(320px,460px)] lg:gap-8">
          <h2 className="text-balance text-center leading-[120%] tracking-[0] text-foreground lg:text-left">
            <span className="block font-alt text-[27px] font-medium lg:text-[54px]">
              Objavi svoju uslugu.
            </span>
            <span className="block font-serif text-[#4b5a6e] leading-[1.6] text-[27px] font-medium italic lg:text-[54px] dark:text-[#A8B4FF]">
              Besplatno i brzo.
            </span>
          </h2>
          <p className="max-w-[46ch] text-center text-base font-normal leading-[150%] tracking-[0] text-[#1c1917] lg:text-center dark:text-[#cbd5e1]">
            Par koraka vas dijeli od novih klijenata. Pošaljite nam informacije o Vašoj usluzi prema pripremljenom šablonu na Viber ili WhatsApp, a
            mi ćemo uraditi sve ostalo za Vas.
          </p>
        </div>

        {/* ── 2-column step grid ── */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((step, idx) => {
            const isFirst = idx === 0;
            const isLast = idx === steps.length - 1;

            return (
              <div
                key={step.number}
                className={cn(
                  "flex flex-col gap-3 rounded-2xl border bg-card px-5 py-5 shadow-sm transition-shadow hover:shadow-md dark:bg-[#0F1017]",
                  isLast && "border-dashed"
                )}
                style={
                  {
                    borderColor: step.accentBorder,
                    // Both label colours use the AA-safe shades so the small
                    // 10 px uppercase text always clears 4.5 : 1.
                    ["--step-label-light" as string]: step.textAccent,
                    ["--step-label-dark" as string]: STEP_LABEL_DARK[idx],
                  } as React.CSSProperties
                }
              >
                {/* Step number badge — step 1 matches copy button / label dark palette */}
                <span
                  className={cn(
                    "inline-flex w-fit rounded-lg px-2.5 py-1 text-[11px] font-bold tracking-widest",
                    idx === 0
                      ? "text-[#2438C9] bg-[rgba(49,75,255,0.09)] dark:bg-[rgba(168,180,255,0.16)] dark:text-[#C7D0FF]"
                      : ""
                  )}
                  style={
                    idx === 0
                      ? undefined
                      : {
                          // `textAccent` is the AA-compliant darker brand shade
                          // used only for text/badge labels (≥ 4.5 : 1 on white).
                          color: step.textAccent,
                          backgroundColor: step.accentBg,
                        }
                  }
                >
                  {step.number}
                </span>

                <div>
                  {/*
                    Removed the 70 % opacity multiplier on the label colour —
                    it dragged green/amber/violet labels below AA contrast on
                    white cards. The slightly darker `textAccent` already gives
                    a calmer feel without sacrificing legibility.
                  */}
                  <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[color:var(--step-label-light)] dark:text-[color:var(--step-label-dark)]">
                    {step.label}
                  </p>
                  <h3 className="mt-0.5 text-[15px] font-semibold leading-snug text-foreground dark:text-white">
                    {step.title}
                  </h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-[#475569] dark:text-[#cbd5e1]">
                    {step.description}
                  </p>
                </div>

                {/* Step 1 — copy button */}
                {isFirst && (
                  <button
                    type="button"
                    onClick={handleCopy}
                    className={cn(
                      "mt-auto inline-flex cursor-pointer items-center gap-2 self-start rounded-md border px-4 py-2.5 text-sm font-semibold transition-all duration-200",
                      copied
                        ? "border-green-500/35 bg-green-50 text-green-700 dark:bg-green-500/12 dark:text-green-400"
                        : [
                            "border-[color:rgba(49,75,255,0.18)] bg-[rgba(49,75,255,0.07)] text-[#314BFF]",
                            "dark:border-[color:rgba(168,180,255,0.38)] dark:bg-[rgba(168,180,255,0.12)] dark:text-[#B8C2FF]",
                            "hover:-translate-y-[1px]",
                          ]
                    )}
                  >
                    {copied ? (
                      <>
                        <Check className="h-4 w-4 shrink-0" strokeWidth={2.5} />
                        Kopirano!
                      </>
                    ) : (
                      <>
                        <ClipboardCopy
                          className="h-4 w-4 shrink-0"
                          strokeWidth={1.75}
                        />
                        Kopiraj poruku
                      </>
                    )}
                  </button>
                )}

                {/* Step 2 — Viber + WhatsApp */}
                {idx === 1 && (
                  <div className="mt-auto flex flex-wrap gap-2.5">
                    {/*
                      Brand greens/violets at saturation 500 fail contrast on
                      light backgrounds. We keep the brand hue in the icon
                      colour (visual recognition) and use darker text shades
                      so the label clears WCAG AA (≥ 4.5 : 1 on white). On dark
                      cards the original lighter brand colour reads fine.
                    */}
                    <a
                      href={`viber://chat?number=${VIBER_NUMBER}`}
                      className="inline-flex items-center gap-2 cursor-pointer rounded-md border px-4 py-2.5 text-sm font-semibold transition-all duration-200 hover:-translate-y-[1px] text-[#4332B0] dark:text-[#A99CFF]"
                      style={{
                        borderColor: "rgba(115,96,242,0.32)",
                        backgroundColor: "rgba(115,96,242,0.10)",
                      }}
                      aria-label="Otvori Viber chat"
                    >
                      <FaViber className="h-[15px] w-[15px] shrink-0 text-[#7360F2]" />
                      Viber
                    </a>
                    <a
                      href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(CONTACT_TEXT)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 cursor-pointer rounded-md border px-4 py-2.5 text-sm font-semibold transition-all duration-200 hover:-translate-y-[1px] text-[#0B6B43] dark:text-[#4ADE80]"
                      style={{
                        borderColor: "rgba(37,211,102,0.32)",
                        backgroundColor: "rgba(37,211,102,0.10)",
                      }}
                      aria-label="Otvori WhatsApp poruku za objavu usluge"
                    >
                      <FaWhatsapp className="h-[15px] w-[15px] shrink-0 text-[#25D366]" />
                      WhatsApp
                    </a>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
